/**
 * @fileoverview One permission row of a Capabilities module card.
 * @description Names the permission in plain words (with its description as a tooltip), flags it
 * "New" or "Inactive", says in a line underneath which parts of the Sales app it controls, and ends
 * with one CapabilityCell per role.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import CapabilityCell from '@/features/capabilities/components/CapabilityCell'
import {
  isChanged,
  isEditable,
  isGranted,
} from '@/features/capabilities/utils/capability-draft.utils'
import {
  actionLabelKey,
  getPermissionControls,
  permissionNameKey,
} from '@/features/capabilities/utils/capability-label.utils'

import type {
  CapabilityDraft,
  CapabilityPermission,
  CapabilityRole,
} from '@/features/capabilities/types/capabilities.types'
import type { TFunction } from 'i18next'

interface CapabilityRowProps {
  permission: CapabilityPermission
  roles: CapabilityRole[]
  /** The edit draft, or null when the page isn't in edit mode. */
  draft: CapabilityDraft | null
  onToggle: (roleCode: string, permissionCode: string) => void
}

const MAX_USES_SHOWN = 2

/**
 * @description The "used for" line under a permission: its first two app actions, then a count of
 * the rest.
 * @param {TFunction} t - The translate function.
 * @param {CapabilityPermission} permission - The permission.
 * @returns {{ text: string; full: string }} The visible text and the full list for a tooltip,
 * both empty when the permission controls nothing in this app.
 */
function describeUses(t: TFunction, permission: CapabilityPermission) {
  const labels = getPermissionControls(permission.code).map((action) =>
    t(actionLabelKey(action), { defaultValue: action }),
  )
  const extra = labels.length - MAX_USES_SHOWN
  const shown = labels.slice(0, MAX_USES_SHOWN).join(', ')

  return {
    text: extra > 0 ? `${shown} ${t('capabilities.uses.andMore', { count: extra })}` : shown,
    full: labels.join(', '),
  }
}

/**
 * @component
 * @param {CapabilityRowProps} props - The permission, the role columns and the edit state.
 * @param {CapabilityPermission} props.permission - The permission this row is for.
 * @param {CapabilityRole[]} props.roles - The role columns.
 * @param {CapabilityDraft | null} props.draft - The edit draft, or null when not editing.
 * @param {(roleCode: string, permissionCode: string) => void} props.onToggle - Called when an
 * editable cell is clicked.
 */
function CapabilityRow({ permission, roles, draft, onToggle }: CapabilityRowProps) {
  const { t } = useTranslation()
  const uses = describeUses(t, permission)

  return (
    <tr
      className="border-b border-row-border last:border-b-0"
      data-testid={`capability-row-${permission.code}`}
    >
      <th
        scope="row"
        className={`px-4 py-2.5 text-left align-middle font-normal ${
          permission.active ? '' : 'opacity-60'
        }`}
      >
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
          <b
            className="text-[13.5px] font-semibold text-ink"
            title={permission.description}
            data-testid={`capability-name-${permission.code}`}
          >
            {t(permissionNameKey(permission.code), { defaultValue: permission.name })}
          </b>
          {permission.isNew && (
            <span
              className="rounded-full bg-info-bg px-2 py-px text-[10.5px] font-semibold text-info"
              data-testid={`capability-new-badge-${permission.code}`}
            >
              {t('capabilities.badge.new')}
            </span>
          )}
          {!permission.active && (
            <span
              className="rounded-full bg-neutral-bg px-2 py-px text-[10.5px] font-semibold text-slate"
              data-testid={`capability-inactive-badge-${permission.code}`}
            >
              {t('capabilities.badge.inactive')}
            </span>
          )}
        </div>
        {uses.text && (
          <div
            className="mt-0.5 text-xs leading-snug text-slate"
            title={uses.full}
            data-testid={`capability-uses-${permission.code}`}
          >
            {uses.text}
          </div>
        )}
      </th>
      {roles.map((role) => (
        <CapabilityCell
          key={role.code}
          roleCode={role.code}
          permissionCode={permission.code}
          granted={isGranted(permission, role.code, draft)}
          locked={role.locked}
          active={permission.active}
          editing={draft !== null && isEditable(role, permission)}
          changed={isChanged(permission, role, draft)}
          onToggle={onToggle}
        />
      ))}
    </tr>
  )
}

export default CapabilityRow
